import React, { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSparks } from '../../contexts/SparksContext';
import AddyAvatar from './AddyAvatar';
import { CELEBRATION_MS, cosmeticIconUrl } from './cosmetics';
import { closeTally, readTally } from './recapTally';
import SparksCount from './SparksCount';
import SparksStarsClimb, { MOVE_MS as CLIMB_MS } from './SparksStarsClimb';
import { newlyAffordable } from './shopNudge';
import { useShop } from './useCosmeticCatalogue';
import useSparksStarsClimb from './useSparksStarsClimb';
import { Button } from '../shadcn/button';

// The recap is one sequence: the Sparks count up (COUNT_MS), then it releases the celebration cards (held everywhere
// else, so they only ever play here), one per Continue, then, once no card has been waiting for CARDS_SETTLE_MS (a late streak reply can still add one), the
// Sparks Stars climb, and last, once the climb has played, the item the lesson made affordable (and the nav's shop
// sticker and bubble, held until then).
const COUNT_MS = 1500;
const CARDS_SETTLE_MS = 600;
const AFTER_CLIMB_MS = 400;

/**
 * End-of-activity screen (lesson, homework, worksheet, pre-quiz): Addy plays the child's celebration, the Sparks
 * earned since the activity started count up, then the new total; then the celebration cards (streak, badge, level)
 * one per Continue; then a Sparks Stars climb and the dearest item it made affordable. The nav "+N" pops stay quiet
 * while it is open. `children` is the finished message and `detail` an optional line under it. `showShop` adds a
 * shop button (not in a live classroom lesson, which it would leave).
 */
export default function LessonRecap({ children, detail, continueLabel, onContinue, showShop = false }) {
  const { loggedInChild } = useAuth();
  const childId = loggedInChild?._id;
  const { enabled, lifetime, balance, equipped, celebrations, setRecapOpen, setCardsReleased, setShopNewsHeld } = useSparks();
  const shop = useShop(enabled ? childId : null);
  const [tally] = useState(() => readTally(childId));
  const { climb, checked } = useSparksStarsClimb(childId, tally);
  const [celebrating, setCelebrating] = useState(true);
  const [counting, setCounting] = useState(false);
  // 'points' (counting up, cards held) -> 'cards' (cards play) -> 'board' (climb and affordable item shown).
  const [stage, setStage] = useState('points');
  const revealed = stage === 'board';
  const [finished, setFinished] = useState(false);

  const earned = tally ? Math.max(0, lifetime - tally.lifetime) : 0;
  const unlocked = tally && shop?.enabled && shop.canBuy ? newlyAffordable(shop.items, tally.balance, balance)[0] : null;

  useEffect(() => {
    closeTally(childId);
    setCounting(true);
    const t = setTimeout(() => setCelebrating(false), CELEBRATION_MS);
    return () => clearTimeout(t);
  }, [childId]);

  useEffect(() => {
    if (!enabled) return undefined;
    setRecapOpen(true);
    const t = setTimeout(() => {
      setCardsReleased(true);
      setStage('cards');
    }, COUNT_MS);
    return () => {
      clearTimeout(t);
      setRecapOpen(false);
      setCardsReleased(false);
    };
  }, [enabled, setRecapOpen, setCardsReleased]);

  const cardsWaiting = celebrations.length > 0;
  useEffect(() => {
    if (stage !== 'cards' || cardsWaiting) return undefined;
    const t = setTimeout(() => setStage('board'), CARDS_SETTLE_MS);
    return () => clearTimeout(t);
  }, [stage, cardsWaiting]);

  // The sequence ends once the board has been read and any climb has played.
  useEffect(() => {
    if (!revealed || !checked || finished) return undefined;
    const t = setTimeout(() => setFinished(true), climb ? CLIMB_MS + AFTER_CLIMB_MS : 0);
    return () => clearTimeout(t);
  }, [revealed, checked, climb, finished]);

  useEffect(() => {
    if (!enabled || finished) return undefined;
    setShopNewsHeld(true);
    return () => setShopNewsHeld(false);
  }, [enabled, finished, setShopNewsHeld]);

  useEffect(() => {
    if (enabled && tally) posthog.capture('lesson_recap_shown', { earned, unlockedKey: unlocked?.key ?? null });
    // Once per recap, with what was known when it opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col items-center text-center px-4 pt-2 pb-6" data-testid="lesson-recap">
      {enabled && <AddyAvatar equipped={equipped} catalogue={shop?.items} size={180} bleed celebrating={celebrating} />}
      {/* The next steps sit right under Addy, before the message and the Sparks card. */}
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <Button onClick={onContinue} className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-5">
          {continueLabel}
        </Button>
        {enabled && showShop && shop?.canBuy && (
          <Button asChild variant="outline" className="border-[#5C3BD1] text-[#5C3BD1] font-bold py-2 px-5">
            <Link to="/shop" onClick={() => posthog.capture('lesson_recap_shop_clicked', { unlockedKey: unlocked?.key ?? null })}>
              Spend in Addy's Shop ✨
            </Link>
          </Button>
        )}
      </div>
      <h2 className="mt-4 text-2xl sm:text-3xl font-bold text-[#2F1E6B]">{children}</h2>
      {detail && <p className="mt-2 max-w-md text-gray-700">{detail}</p>}

      {enabled && (
        <div className="mt-4 w-full max-w-sm rounded-2xl border-2 border-[#FFD966] bg-white p-4 shadow">
          {tally && (
            <>
              <div className="text-sm text-gray-500">You earned</div>
              <div className="text-4xl font-extrabold text-[#2F1E6B]" data-testid="lesson-recap-earned">
                <SparksCount value={counting ? earned : 0} reserve={earned} duration={1200} /> <span aria-hidden>✨</span>
              </div>
            </>
          )}
          <div className="mt-1 text-sm text-gray-600">
            You have <span className="font-bold text-[#2F1E6B]"><SparksCount value={balance} /></span> Sparks
          </div>
          {climb && revealed && <SparksStarsClimb key={`${climb.kind}-${climb.to}-${climb.by}`} climb={climb} catalogue={shop?.items} />}
          {unlocked && finished && (
            <div className="mt-3 flex items-center gap-3 rounded-xl bg-[#FFF6D6] px-3 py-2 text-left" data-testid="lesson-recap-unlocked">
              <img src={cosmeticIconUrl(unlocked)} alt="" className="h-12 w-12 shrink-0 object-contain" />
              <div className="text-sm font-semibold text-[#2F1E6B]">You can now buy the {unlocked.name}!</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
